import {Component, inject, OnInit, signal, ChangeDetectionStrategy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {VwuiAngularModule, VwuiModalService} from '@bvgo-ict/vwui-angular';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {filter, finalize, switchMap, take} from 'rxjs/operators';
import {forkJoin} from 'rxjs';

import {
    LoadingSpinnerService,
    ProjectAnalysisDefinitionService,
    ProjectTypeService,
    ToastService
} from '../../services';
import {
    BackHeaderComponent,
    CopyProjectAnalysisModalComponent,
    CreateUpdateProjectTypeModalComponent,
    DeleteItemModalComponent,
    PublishQuickScanVersionModalComponent
} from '../../components';
import {CopyProjectAnalysisModalResult} from '../../components/copy-project-analysis-modal/copy-project-analysis-modal.component';
import {ProjectAnalysisVersion, ProjectType} from '../../models/project-analysis';

@Component({
    selector: 'app-page-project-type',
    templateUrl: './page-project-type.component.html',
    styleUrls: ['./page-project-type.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, TranslatePipe, VwuiAngularModule, BackHeaderComponent]
})
export class PageProjectTypeComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly projectTypeService = inject(ProjectTypeService);
    private readonly definitionService = inject(ProjectAnalysisDefinitionService);
    private readonly modalService = inject(VwuiModalService);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);
    public readonly loadingSpinnerService = inject(LoadingSpinnerService);

    public readonly projectType = signal<ProjectType | null>(null);
    public readonly versions = signal<ProjectAnalysisVersion[]>([]);

    private projectTypeId: number;

    ngOnInit(): void {
        this.route.params.subscribe((params) => {
            this.projectTypeId = Number(params['projectTypeId']);
            this.loadData();
        });
    }

    public navigateToProjectTypes(): void {
        this.router.navigate(['/beheer', 'project-types']);
    }

    public openEditModal(): void {
        const projectType = this.projectType();
        if (!projectType) {
            return;
        }

        const modal = this.modalService.open(CreateUpdateProjectTypeModalComponent, {
            closeOnBackdropClick: false,
            data: {
                title: 'PAGE_PROJECT_TYPES.update_item.title',
                saveText: 'PAGE_PROJECT_TYPES.update_item.save',
                data: projectType
            }
        });

        modal.afterClosed
            .pipe(
                take(1),
                filter(Boolean),
                switchMap((updatedProjectType: ProjectType) =>
                    this.projectTypeService.update(projectType.id, updatedProjectType)
                )
            )
            .subscribe(() => {
                this.toastService.openSuccess(this.translateService.instant('PAGE_PROJECT_TYPES.type_modified'));
                this.loadData();
            });
    }

    public editVersion(version: ProjectAnalysisVersion): void {
        // TODO route to /beheer/project-types/:id/version/:versionId once new editor exists.
        this.router.navigate(['/beheer', 'project-analysis', this.projectTypeId, version.versionId]);
    }

    public openPublishModal(version: ProjectAnalysisVersion): void {
        const modal = this.modalService.open(PublishQuickScanVersionModalComponent, {data: version});

        modal.afterClosed
            .pipe(take(1), filter(Boolean))
            .subscribe(() => {
                this.definitionService.publishDefinition(this.projectTypeId, version.versionId)
                    .subscribe(() => {
                        this.toastService.openSuccess(
                            this.translateService.instant('PAGE_PROJECT_TYPE.toast.version_published')
                        );
                        this.loadData();
                    });
            });
    }

    public openDeleteVersionModal(version: ProjectAnalysisVersion): void {
        const modal = this.modalService.open(DeleteItemModalComponent, {
            data: {
                title: 'PAGE_PROJECT_TYPE.versions.delete.title',
                description: 'PAGE_PROJECT_TYPE.versions.delete.description',
                extraDescription: null
            }
        });

        modal.afterClosed
            .pipe(
                take(1),
                filter(Boolean),
                switchMap(() => this.definitionService.deleteVersion(this.projectTypeId, version.versionId))
            )
            .subscribe(() => {
                this.toastService.openSuccess(
                    this.translateService.instant('PAGE_PROJECT_TYPE.toast.version_deleted')
                );
                this.loadData();
            });
    }

    public createFirstVersion(): void {
        const projectType = this.projectType();
        if (!projectType) {
            return;
        }

        this.loadingSpinnerService.show();
        this.definitionService.createFirstVersion(projectType)
            .pipe(finalize(() => this.loadingSpinnerService.hide()))
            .subscribe((version) => {
                this.toastService.openSuccess(
                    this.translateService.instant('PAGE_PROJECT_TYPE.toast.version_created')
                );
                this.editVersion(version);
            });
    }

    public openCopyFromModal(): void {
        const projectType = this.projectType();
        if (!projectType) {
            return;
        }

        const modal = this.modalService.open(CopyProjectAnalysisModalComponent, {
            closeOnBackdropClick: false,
            data: {targetProjectType: projectType}
        });

        modal.afterClosed
            .pipe(
                take(1),
                filter(Boolean),
                switchMap((result: CopyProjectAnalysisModalResult) =>
                    this.definitionService.copyFromVersion(projectType, result.sourceProjectTypeId, result.sourceVersionId)
                )
            )
            .subscribe((version) => {
                this.toastService.openSuccess(
                    this.translateService.instant('PAGE_PROJECT_TYPE.toast.version_copied')
                );
                this.editVersion(version);
            });
    }

    public trackVersion(_index: number, item: ProjectAnalysisVersion): number {
        return item.id;
    }

    private loadData(): void {
        this.loadingSpinnerService.show();
        forkJoin({
            projectType: this.projectTypeService.getById(this.projectTypeId).pipe(take(1)),
            versions: this.definitionService.getVersions(this.projectTypeId)
        })
            .pipe(finalize(() => this.loadingSpinnerService.hide()))
            .subscribe({
                next: ({projectType, versions}) => {
                    this.projectType.set(projectType ?? null);
                    this.versions.set([...versions].sort((a, b) => b.versionId - a.versionId));
                },
                error: () => {
                    this.projectType.set(null);
                    this.versions.set([]);
                }
            });
    }
}
